"use client";

import { useState } from "react";
import { usePathname } from "next/navigation";
import { CALCULATOR_FAQS } from "@/lib/calculator-faqs-data";
import { JsonLd, generateFaqSchema } from "@/components/JsonLd";

interface FaqSectionProps {
  calculatorId: string;
}

export default function FaqSection({ calculatorId }: FaqSectionProps) {
  const faqs = CALCULATOR_FAQS[calculatorId];
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const pathname = usePathname();

  if (!faqs || faqs.length === 0) return null;

  const siteUrl = process.env.NEXT_PUBLIC_SITE_URL ?? "https://www.meazora.com";
  const sanitizedPath = pathname ? (pathname.endsWith("/") ? pathname : `${pathname}/`) : "/";
  const currentUrl = `${siteUrl}${sanitizedPath}`;
  const faqSchema = generateFaqSchema(faqs, currentUrl);

  return (
    <div className="space-y-6 mt-16 w-full">
      <JsonLd data={faqSchema} />
      <div className="flex items-center gap-4">
        <h3 className="text-xl font-extrabold uppercase tracking-wider text-slate-900 dark:text-white font-heading-condensed whitespace-nowrap">
          Frequently Asked Questions
        </h3>
        <div className="flex-1 h-px bg-[var(--line-strong)]" />
      </div>
      <p className="text-xs text-slate-500 dark:text-slate-400 font-medium -mt-4">
        Technical building code (IRC/ASTM) references and trade-tested guidance for your project.
      </p>

      <div className="space-y-3.5">
        {faqs.map((faq, index) => {
          const isOpen = openIndex === index;
          const panelId = `faq-panel-${index}`;
          return (
            <div
              key={index}
              className="rounded-lg border border-[var(--line)] bg-[var(--surface)] overflow-hidden transition-all duration-200"
            >
              <button
                type="button"
                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left font-bold text-[var(--ink)] hover:bg-[var(--surface-muted)] transition-colors"
                onClick={() => setOpenIndex(isOpen ? null : index)}
                aria-expanded={isOpen}
                aria-controls={panelId}
              >
                <span className="text-sm sm:text-base leading-snug">{faq.question}</span>
                <span className={`text-[var(--ink-soft)] transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}>
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </span>
              </button>

              <div
                id={panelId}
                className={`transition-all duration-200 ease-in-out ${
                  isOpen ? "max-h-[500px] border-t border-[var(--line)]" : "max-h-0"
                } overflow-hidden`}
              >
                <div className="px-5 py-4 space-y-3 bg-[var(--surface-muted)]/50">
                  <p className="text-sm leading-relaxed text-[var(--ink-soft)]">
                    {faq.answer}
                  </p>
                  
                  {/* Verified Source Citation Footer */}
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 text-[10px] font-medium text-[var(--ink-faint)] border-t border-[var(--line)]/50 pt-3 mt-1">
                    <span className="inline-flex items-center gap-1 text-[var(--accent)] font-bold uppercase tracking-wider text-[9px]">
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                      Verified Source
                    </span>
                    <span className="text-slate-350 dark:text-slate-700 select-none">•</span>
                    <span>{faq.sourceName}</span>
                    <span className="text-slate-350 dark:text-slate-700 select-none">•</span>
                    <a
                      href={faq.sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline hover:text-slate-900 dark:hover:text-white transition-colors"
                    >
                      Audit Link
                    </a>
                    <span className="text-slate-350 dark:text-slate-700 select-none">•</span>
                    <span>Consulted: {faq.retrievedDate}</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
